import { useEffect, useState, type ReactNode } from "react";
import { Check, Circle, CircleDot } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import MobileHeader from "../layout/MobileHeader";
import { ShellBrand } from "../layout/ShellBrand";
import { MobileContent } from "../layout/MobileContent";
import {
  dayExpandItems,
  presentChip,
  type BarPhase,
  stepLabel,
  type PipelineItem,
  type PipelineStep,
  type StepMark,
} from "./dayPipeline";
import {
  DOW_HEADER,
  buildMonthRows,
  categoryOf,
  cellKey,
  dayAriaLabel,
  dowLabel,
  dowOf,
  extractTitles,
  formatMd,
  isSameCell,
  lanesForWeek,
  todayCell,
  type LaneChip,
} from "./derive";
import { LegendEditDrawer } from "./LegendEditDrawer";
import { WorkflowDrawer } from "./WorkflowDrawer";
import {
  groupLegend,
  legendChipClassName,
  legendChipStyle,
} from "./legendAdapter";
import {
  barPaintStyle,
  faceTagStyle,
  findLegendItem,
  railColor,
  softChipStyle,
  washStyle,
} from "./legendPaint";
import type { BoardLegendItem } from "./legendTypes";
import { BoardLegendProvider, useBoardLegend } from "./useBoardLegend";
import { BoardWorkflowProvider, useBoardWorkflow } from "./useBoardWorkflow";
import { useFounderMonth } from "./useFounderMonth";
import { useOverlayScrollLock } from "./useOverlayScrollLock";
import type { Chip, DayCell, FounderMonth, LineKey, WeekData } from "./types";

type Tone = { dot: string; chip: string; rail: string };

const TONES = {
  gx: {
    dot: "bg-[#c47b12]",
    chip: "bg-[#f6d7a4] text-[#5c2e00] dark:bg-[#5c3a12] dark:text-[#ffe0b0]",
    rail: "#e4d2b4",
  },
  tw: {
    dot: "bg-[#1565b8]",
    chip: "bg-[#c5ddf8] text-[#0a335c] dark:bg-[#123656] dark:text-[#d6ebff]",
    rail: "#c5d4e8",
  },
  cg: {
    dot: "bg-[#5e5a52]",
    chip: "bg-[#e3dfd4] text-[#2a2722] dark:bg-[#3c3934] dark:text-[#f3efe6]",
    rail: "#d9d6d0",
  },
  you: {
    dot: "bg-[#c62828]",
    chip: "bg-[#f7c6c6] text-[#7a1010] dark:bg-[#5a1c1c] dark:text-[#ffd0d0]",
    rail: "#e7c8c8",
  },
  co: {
    dot: "bg-[#1b7a45]",
    chip: "bg-[#b6e8ce] text-[#084028] dark:bg-[#0e3d26] dark:text-[#c9f6de]",
    rail: "#c5dfd0",
  },
  xyz: {
    dot: "bg-[#7b3fa8]",
    chip: "bg-[#e6d0f4] text-[#4a1570] dark:bg-[#3d2054] dark:text-[#f2dcff]",
    rail: "#e0d0ea",
  },
  cosy: {
    dot: "bg-[#0e7c78]",
    chip: "bg-[#b7e8e4] text-[#084440] dark:bg-[#0c3c3a] dark:text-[#c9f6f2]",
    rail: "#c5e0de",
  },
  talk: {
    dot: "bg-[#c62828]",
    chip: "bg-[#f7c6c6] text-[#7a1010] dark:bg-[#5a1c1c] dark:text-[#ffd0d0]",
    rail: "#e7c8c8",
  },
  soft: {
    dot: "bg-[#737373]",
    chip: "bg-[#e7e7e7] text-[#2c2c2c] dark:bg-[#333] dark:text-[#eee]",
    rail: "#e4e4e2",
  },
  geo: {
    dot: "bg-[#d17a1a]",
    chip: "bg-[#f8d7b0] text-[#6a3808] dark:bg-[#5a3814] dark:text-[#ffe0bc]",
    rail: "#ead4b8",
  },
  nm: {
    dot: "bg-[#0a5fbf]",
    chip: "bg-[#c5dcf8] text-[#08315c] dark:bg-[#123056] dark:text-[#d6e8ff]",
    rail: "#c5d8ee",
  },
} as const satisfies Record<string, Tone>;

/** Product ideas: dashed stroke only. Fill stays transparent (or white in dark). */
const IDEA_OUTLINE =
  "border border-dashed border-[#C4A574] bg-transparent text-[#5B4A2A] dark:bg-white dark:text-[#5B4A2A]";

const IDEA_TONE: Tone = {
  dot: "bg-transparent",
  chip: IDEA_OUTLINE,
  rail: "#e6d7c4",
};

const HELPER_CHIP =
  "bg-[#F2F2F7] text-[#6E6E73] dark:bg-muted dark:text-muted-foreground";

const CHIP_BG: Record<LineKey, string> = {
  gx: TONES.gx.chip,
  tw: TONES.tw.chip,
  cg: TONES.cg.chip,
  xyz: TONES.xyz.chip,
  cosy: TONES.cosy.chip,
  geo: TONES.geo.chip,
  nm: TONES.nm.chip,
  you: TONES.you.chip,
  co: TONES.co.chip,
  talk: TONES.talk.chip,
  ip: TONES.gx.chip,
  idea: IDEA_TONE.chip,
};

/** Month bars: solid fill, 18px, 3px radius, light on-bar type. Ideas stay an outline. */
const BAR: Record<LineKey, string> = {
  gx: "bg-[#db7a00] text-white",
  tw: "bg-[#5856d6] text-white",
  cg: "bg-[#8e8e93] text-white",
  xyz: "bg-[#7b3fa8] text-white",
  cosy: "bg-[#0e7c78] text-white",
  geo: "bg-[#d17a1a] text-white",
  nm: "bg-[#007aff] text-white",
  you: "bg-[#ff3b30] text-white",
  co: "bg-[#2aa84a] text-white",
  talk: "bg-[#ff3b30] text-white",
  ip: "bg-[#db7a00] text-white",
  idea: IDEA_OUTLINE,
};

/** Same solids as the month bars. Light type stays white on the legend chip. */
const LEGEND_FILL = {
  gx: "bg-[#db7a00]",
  tw: "bg-[#5856d6]",
  cg: "bg-[#8e8e93]",
  xyz: "bg-[#7b3fa8]",
  cosy: "bg-[#0e7c78]",
  geo: "bg-[#d17a1a]",
  nm: "bg-[#007aff]",
  you: "bg-[#ff3b30]",
  co: "bg-[#2aa84a]",
} as const;

/**
 * Day-expand face tokens. In-repo source (ops/workbench/DESIGN_TOKENS.md
 * is not in this tree): legend solids, ~10% wash of the same hue, chrome type.
 */
const FACE_TAG = {
  gx: `${LEGEND_FILL.gx} text-white`,
  tw: `${LEGEND_FILL.tw} text-white`,
  cg: `${LEGEND_FILL.cg} text-white`,
  xyz: `${LEGEND_FILL.xyz} text-white`,
  cosy: `${LEGEND_FILL.cosy} text-white`,
  geo: `${LEGEND_FILL.geo} text-white`,
  nm: `${LEGEND_FILL.nm} text-white`,
  you: `${LEGEND_FILL.you} text-white`,
  co: `${LEGEND_FILL.co} text-white`,
  idea: IDEA_OUTLINE,
  soft: HELPER_CHIP,
} as const;

const FACE_WASH = {
  gx: "bg-[#fbf2e6] dark:bg-[#3d2a12]",
  tw: "bg-[#eeeefb] dark:bg-[#1c1c3a]",
  cg: "bg-[#f6f6f6] dark:bg-[#2c2c2c]",
  you: "bg-[#ffeeea] dark:bg-[#3a1c1c]",
  co: "bg-[#eaf6ed] dark:bg-[#12301c]",
  xyz: "bg-[#f2ecf6] dark:bg-[#2a1c36]",
  cosy: "bg-[#e7f2f2] dark:bg-[#12302e]",
  geo: "bg-[#faf2e8] dark:bg-[#3a2812]",
  nm: "bg-[#e8f3fc] dark:bg-[#12283a]",
  soft: "bg-[#f4f4f3] dark:bg-[#2a2a2a]",
  idea: "bg-[#fffdf8] dark:bg-card",
} as const;

const TYPE = {
  title:
    "text-[15px] font-semibold leading-snug text-[#3a3a38] dark:text-foreground",
  node: "mt-2 text-[13px] leading-tight font-medium text-[#5c5c5a] dark:text-muted-foreground",
  nodeNow:
    "mt-2 text-[13px] leading-tight font-semibold text-[#1a4a7a] dark:text-[#c5d9ef]",
  nodeBlocked: "mt-2 text-[13px] leading-tight font-medium text-[#8B1A1A]",
  body: "text-[12px] leading-snug font-normal text-[#666] dark:text-muted-foreground",
  bodyNow:
    "text-[12px] leading-snug font-normal text-[#1a4a7a] dark:text-[#c5d9ef]",
  bodyDone:
    "text-[12px] leading-snug font-normal text-[#0a5c36] dark:text-[#8ee0b0]",
  bodyBlocked: "text-[12px] leading-snug font-normal text-[#8B1A1A]",
  empty:
    "text-[12px] leading-snug font-normal text-[#8e8e93] dark:text-muted-foreground",
} as const;

/** Accessible row names only. The legend shows chips, not layer captions. */
const LEGEND_GROUP = {
  biz: "业务线色标",
  idea: "产品想法色标",
  matter: "事项类色标",
} as const;

const CHIP_SHAPE =
  "relative z-10 mx-1 flex h-[18px] min-w-0 items-center gap-0.5 overflow-hidden truncate rounded-[3px] px-1 text-left text-[12px] font-medium leading-none";

type DrawerState =
  | { kind: "detail"; chip: Chip; cell: DayCell }
  | { kind: "list"; chips: Chip[]; cell: DayCell };

const shellFont = {
  fontFamily:
    '-apple-system,BlinkMacSystemFont,"SF Pro Text","PingFang SC","Hiragino Sans GB","Noto Sans CJK SC",ui-sans-serif,system-ui,sans-serif',
} as const;

export const FounderMonthHome = () => (
  <BoardLegendProvider>
    <BoardWorkflowProvider>
      <FounderMonthBoard />
    </BoardWorkflowProvider>
  </BoardLegendProvider>
);

const FounderMonthBoard = () => {
  const { month, notice } = useFounderMonth();
  const rows = buildMonthRows(month);
  const [selected, setSelected] = useState<DayCell>(() => todayCell());

  useEffect(() => {
    setSelected((current) => {
      const stillInside = buildMonthRows(month).some((row) =>
        row.cells.some((cell) => isSameCell(cell, current) && !cell.out),
      );
      return stillInside ? current : todayCell(month);
    });
  }, [month]);
  const [expanded, setExpanded] = useState(true);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  useEffect(() => {
    if (!drawer) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setDrawer(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer]);

  const selectDay = (cell: DayCell) => {
    if (cell.out) return;
    setSelected(cell);
    setExpanded(true);
  };

  return (
    <div
      className="mx-auto max-w-[1080px] text-[#1d1d1f] dark:text-foreground"
      style={shellFont}
      data-month-source={month.sources === "board_months" ? "db" : "fallback"}
    >
      <section className="mb-4">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <h2 className="text-[20px] font-semibold tracking-tight">
            {month.title}
          </h2>
          <CategoryLegend />
        </div>
        {notice ? (
          <p role="status" className="mb-2 text-[12px] text-muted-foreground">
            {notice}
          </p>
        ) : null}
        <div className="overflow-hidden rounded-lg border border-[#e6e6e4] bg-white dark:border-border dark:bg-card">
          <div
            className="grid grid-cols-7"
            role="grid"
            aria-label={`${month.title}日历`}
          >
            {DOW_HEADER.map((label, index) => (
              <div
                key={label}
                role="columnheader"
                className={cn(
                  "border-b border-[#e6e6e4] py-2 text-center text-[12px] font-medium tracking-wide text-[#666] dark:border-border dark:text-muted-foreground",
                  index < 6 && "border-r",
                )}
              >
                {label}
              </div>
            ))}
            {rows.map((row, index) => (
              <WeekRow
                key={row.wk + row.cells[0].d}
                rowWk={row.wk}
                cells={row.cells}
                month={month}
                selected={selected}
                expanded={expanded}
                isLast={index === rows.length - 1}
                onSelect={selectDay}
                onChip={(chip, cell) =>
                  setDrawer({ kind: "detail", chip, cell })
                }
                onCollapse={() => setExpanded(false)}
              />
            ))}
          </div>
        </div>
      </section>

      {drawer ? (
        <ChipDrawer
          month={month}
          state={drawer}
          onClose={() => setDrawer(null)}
          onOpenChip={(chip) =>
            setDrawer({ kind: "detail", chip, cell: drawer.cell })
          }
          onExpandDay={() => {
            const cell = drawer.cell;
            setDrawer(null);
            selectDay(cell);
          }}
        />
      ) : null}
    </div>
  );
};

export const FounderMonthHomeMobile = () => {
  const { darkModeLogo, lightModeLogo } = useConfigurationContext();
  return (
    <>
      <MobileHeader>
        <div className="flex items-center gap-2 py-3 text-[#1a1a1a] no-underline dark:text-secondary-foreground">
          <img
            className="[.light_&]:hidden h-6"
            src={darkModeLogo}
            alt="毫厘"
          />
          <img
            className="[.dark_&]:hidden h-6"
            src={lightModeLogo}
            alt="毫厘"
          />
          <h1>
            <ShellBrand />
          </h1>
        </div>
      </MobileHeader>
      <MobileContent>
        <FounderMonthHome />
      </MobileContent>
    </>
  );
};

const LegendChip = ({
  item,
  onEdit,
}: {
  item: BoardLegendItem;
  onEdit: (item: BoardLegendItem) => void;
}) => (
  <span
    role="button"
    tabIndex={0}
    aria-label={`编辑图例 ${item.label}`}
    className={legendChipClassName(item)}
    style={legendChipStyle(item)}
    onClick={() => onEdit(item)}
    onKeyDown={(event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onEdit(item);
    }}
  >
    {item.label}
  </span>
);

const CategoryLegend = () => {
  const { items, source, save } = useBoardLegend();
  const [editing, setEditing] = useState<BoardLegendItem | null>(null);
  const [flow, setFlow] = useState<BoardLegendItem | null>(null);
  const groups = groupLegend(items);
  const openEdit = (item: BoardLegendItem) => {
    setFlow(null);
    setEditing(item);
  };
  const openFlow = (item: BoardLegendItem) => {
    setEditing(null);
    setFlow(item);
  };

  return (
    <>
      <div
        role="group"
        aria-label="分类图例"
        data-legend-source={source}
        className="flex max-w-[680px] flex-col items-end gap-1 text-[12px]"
      >
        <div
          role="group"
          aria-label={LEGEND_GROUP.biz}
          className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1"
        >
          {groups.biz.map((item) => (
            <LegendChip key={item.token} item={item} onEdit={openEdit} />
          ))}
        </div>
        <div
          role="group"
          aria-label={LEGEND_GROUP.idea}
          className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1"
        >
          {groups.idea.map((item) => (
            <LegendChip key={item.token} item={item} onEdit={openEdit} />
          ))}
        </div>
        <div
          role="group"
          aria-label={LEGEND_GROUP.matter}
          className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1"
        >
          {groups.matter.map((item) => (
            <LegendChip key={item.token} item={item} onEdit={openEdit} />
          ))}
        </div>
      </div>
      {editing ? (
        <LegendEditDrawer
          item={editing}
          onClose={() => setEditing(null)}
          onSave={save}
          onOpenFlow={() => openFlow(editing)}
        />
      ) : null}
      {flow ? (
        <WorkflowDrawer item={flow} onClose={() => setFlow(null)} />
      ) : null}
    </>
  );
};

const WeekRow = ({
  rowWk,
  cells,
  month,
  selected,
  expanded,
  isLast,
  onSelect,
  onChip,
  onCollapse,
}: {
  rowWk: string;
  cells: DayCell[];
  month: FounderMonth;
  selected: DayCell;
  expanded: boolean;
  isLast: boolean;
  onSelect: (cell: DayCell) => void;
  onChip: (chip: Chip, cell: DayCell) => void;
  onCollapse: () => void;
}) => {
  const showInsert = expanded && selected.wk === rowWk && !selected.out;
  return (
    <>
      <WeekGrid
        cells={cells}
        month={month}
        selected={selected}
        closeBottom={isLast && !showInsert}
        onSelect={onSelect}
        onChip={onChip}
      />
      {showInsert ? (
        <WeekInsert cell={selected} month={month} onCollapse={onCollapse} />
      ) : null}
    </>
  );
};

const WeekGrid = ({
  cells,
  month,
  selected,
  closeBottom,
  onSelect,
  onChip,
}: {
  cells: DayCell[];
  month: FounderMonth;
  selected: DayCell;
  closeBottom: boolean;
  onSelect: (cell: DayCell) => void;
  onChip: (chip: Chip, cell: DayCell) => void;
}) => {
  const lanes = lanesForWeek(cells, month);
  const today = todayCell(month);
  return (
    <div
      className="col-span-7 grid min-h-[96px] grid-cols-7 gap-x-0 gap-y-1 max-md:min-h-[80px]"
      style={{
        gridTemplateRows: `auto repeat(${lanes.length}, 18px) minmax(0.5rem, 1fr)`,
      }}
    >
      {cells.map((cell, col) => (
        <DayFrame
          key={cellKey(cell)}
          cell={cell}
          col={col}
          closeBottom={closeBottom}
          selected={isSameCell(cell, selected)}
          today={isSameCell(cell, today)}
          onSelect={() => onSelect(cell)}
        />
      ))}
      {cells.map((cell, col) => (
        <DayHeader
          key={`h-${cellKey(cell)}`}
          cell={cell}
          col={col}
          selected={isSameCell(cell, selected)}
          today={isSameCell(cell, today)}
          onSelect={() => onSelect(cell)}
        />
      ))}
      {lanes.map((lane, laneIndex) =>
        lane.map((item) => (
          <ChipButton
            key={`${item.chip.id}-${item.start}`}
            item={item}
            row={laneIndex + 2}
            cell={cells[item.start]}
            month={month}
            onOpen={() => onChip(item.chip, cells[item.start])}
          />
        )),
      )}
      <div
        aria-hidden
        className="pointer-events-none h-2"
        style={{ gridColumn: "1 / -1", gridRow: lanes.length + 2 }}
      />
    </div>
  );
};

const DayFrame = ({
  cell,
  col,
  closeBottom,
  selected,
  today,
  onSelect,
}: {
  cell: DayCell;
  col: number;
  closeBottom: boolean;
  selected: boolean;
  today: boolean;
  onSelect: () => void;
}) => {
  const weekend = dowOf(cell) === 0 || dowOf(cell) === 6;
  return (
    <div
      role="gridcell"
      style={{ gridColumn: col + 1, gridRow: "1 / -1" }}
      className={cn(
        "relative z-0 bg-white dark:bg-card",
        col < 6 && "border-r border-[#e6e6e4] dark:border-border",
        !closeBottom && "border-b border-[#e6e6e4] dark:border-border",
        cell.out && "bg-[#f3f3f1] dark:bg-muted/30",
        !cell.out && "cursor-pointer",
        weekend &&
          !cell.out &&
          !selected &&
          !today &&
          "bg-[#f3f3f1] dark:bg-zinc-900/40",
        !cell.out && selected && "bg-[var(--chrome-sel-bg)] dark:bg-[#152433]",
      )}
      onClick={() => {
        if (!cell.out) onSelect();
      }}
    />
  );
};

const DayHeader = ({
  cell,
  col,
  selected,
  today,
  onSelect,
}: {
  cell: DayCell;
  col: number;
  selected: boolean;
  today: boolean;
  onSelect: () => void;
}) => {
  const weekend = dowOf(cell) === 0 || dowOf(cell) === 6;
  return (
    <div
      className="pointer-events-none relative z-10 flex justify-end px-2 pt-2"
      style={{ gridColumn: col + 1, gridRow: 1 }}
    >
      <button
        type="button"
        disabled={cell.out}
        aria-label={dayAriaLabel(cell)}
        aria-pressed={selected}
        aria-current={today ? "date" : undefined}
        className={cn(
          "pointer-events-auto inline-flex size-6 items-center justify-center rounded-full bg-transparent text-[13px] font-medium tabular-nums disabled:cursor-default",
          cell.out && "text-[#aeaeb2]",
          !cell.out &&
            !today &&
            !selected &&
            weekend &&
            "text-[#8e8e93] dark:text-muted-foreground",
          !cell.out &&
            !today &&
            !selected &&
            !weekend &&
            "text-[#1a1a1a] dark:text-foreground",
          selected &&
            !today &&
            "bg-[var(--chrome-sel)] font-semibold text-white",
          today && "bg-[var(--chrome-today)] font-semibold text-white",
        )}
        onClick={(event) => {
          event.stopPropagation();
          if (!cell.out) onSelect();
        }}
      >
        {cell.d}
      </button>
    </div>
  );
};

const ChipButton = ({
  item,
  row,
  cell,
  month,
  onOpen,
}: {
  item: LaneChip;
  row: number;
  cell: DayCell;
  month: FounderMonth;
  onOpen: () => void;
}) => {
  const { items } = useBoardLegend();
  const { currentNodeTextFor } = useBoardWorkflow();
  const legend = findLegendItem(items, { lineKey: item.chip.lineKey });
  const spanning = item.end > item.start;
  const presented = presentChip(item.chip, cell, month);
  const current = currentNodeTextFor(item.chip.workflowRunId);
  return (
    <button
      type="button"
      title={current ? `${item.chip.title} · ${current}` : item.chip.title}
      aria-label={
        current
          ? `${presented.label} ${item.chip.short} ${current}`
          : `${presented.label} ${item.chip.short}`
      }
      data-span={spanning ? "true" : undefined}
      data-line-key={item.chip.lineKey}
      className={cn(CHIP_SHAPE, BAR[item.chip.lineKey], spanning && "z-20")}
      style={{
        gridColumn: `${item.start + 1} / ${item.end + 2}`,
        gridRow: row,
        ...barPaintStyle(legend),
      }}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      <StatusGlyph phase={presented.phase} />
      <span className="min-w-0 truncate">{item.chip.short}</span>
    </button>
  );
};

const PHASE_TEXT: Record<BarPhase, string> = {
  done: "text-[#0a5c36] dark:text-[#8ee0b0]",
  now: "text-[#1a4a7a] dark:text-[#c5d9ef]",
  later: "text-[#8e8e93] dark:text-muted-foreground",
};

const PHASE_ICON = {
  done: Check,
  now: CircleDot,
  later: Circle,
} as const;

/** Same size and stroke as the month-bar check. Color only when the bar itself is not the fill. */
function StatusGlyph({
  phase,
  tone = false,
}: {
  phase: BarPhase;
  tone?: boolean;
}) {
  const Icon = PHASE_ICON[phase];
  return (
    <Icon
      aria-hidden
      className={cn("size-3 shrink-0", tone && PHASE_TEXT[phase])}
      strokeWidth={2.5}
    />
  );
}

const DOT_BLOCKED = "border-[#E5484D] bg-[#E5484D]";

function stepPhase(mark: StepMark): BarPhase {
  if (mark === "done") return "done";
  if (mark === "now") return "now";
  return "later";
}

function blockedCopy(step: PipelineStep) {
  return /阻塞|疑问|待你|卡住/.test(`${step.name}${step.detail}`);
}

const stepDetail = (detail: string, mark: StepMark) => {
  if (!detail || detail === stepLabel(mark) || detail === "—／未开") return "";
  return detail;
};

function businessTone(
  name: string,
  items: BoardLegendItem[],
): {
  label: string;
  tone: Tone;
  tag: string;
  wash: string;
  tagStyle?: ReturnType<typeof faceTagStyle>;
  washStyle?: ReturnType<typeof washStyle>;
} {
  const found = categoryOf(name);
  const legend = findLegendItem(items, { lineKey: found?.key });
  const painted = {
    tagStyle: faceTagStyle(legend),
    washStyle: washStyle(legend),
  };
  if (!found)
    return {
      label: name,
      tone: TONES.soft,
      tag: FACE_TAG.soft,
      wash: FACE_WASH.soft,
      ...painted,
    };
  if (found.kind === "idea")
    return {
      label: found.label,
      tone: IDEA_TONE,
      tag: FACE_TAG.idea,
      wash: FACE_WASH.idea,
      ...painted,
    };
  if (found.key === "soft")
    return {
      label: found.label,
      tone: { ...TONES.soft, chip: HELPER_CHIP },
      tag: FACE_TAG.soft,
      wash: FACE_WASH.soft,
      ...painted,
    };
  const key = found.key in TONES ? found.key : "soft";
  const tone = key in TONES ? TONES[key as keyof typeof TONES] : TONES.soft;
  return {
    label: found.label,
    tone: { ...tone, rail: railColor(legend, tone.rail) },
    tag: FACE_TAG[key as keyof typeof FACE_TAG] ?? FACE_TAG.soft,
    wash: FACE_WASH[key as keyof typeof FACE_WASH] ?? FACE_WASH.soft,
    ...painted,
  };
}

function looseAction(text: string) {
  const raw = text.match(
    /扫\s*[+＋]\s*ship|审美\s*[+＋]\s*(?:点名\s*)?ship|统一定时|前置审|定时出/,
  )?.[0];
  if (!raw) return undefined;
  return raw
    .replace(/\s+/g, "")
    .replace(/＋/g, "+")
    .replace("点名ship", "ship");
}

function itemFace(item: PipelineItem, items: BoardLegendItem[]) {
  const business = businessTone(item.business, items);
  const titles = extractTitles(item.theme);
  const titled = titles.length
    ? titles.map((title) => `《${title}》`).join("")
    : "";
  const colon = item.theme.match(/^([^：]{2,8})：(.+)$/);
  const theme = titled || (colon ? colon[2].trim() : item.theme);
  const action =
    item.action ??
    looseAction(item.theme) ??
    (colon ? colon[1].trim() : undefined);
  return {
    matterBeside:
      (item.type === "you" || item.type === "talk") &&
      business.label !== "交流",
    businessLabel: business.label,
    tone: business.tone,
    tag: business.tag,
    wash: business.wash,
    tagStyle: business.tagStyle,
    washStyle: business.washStyle,
    theme,
    action: action && action !== theme ? action : undefined,
  };
}

function isImageUrl(url: string) {
  return /^https:\/\/\S+\.(png|jpe?g|gif|webp)(\?\S*)?$/i.test(url);
}

const WeekInsert = ({
  cell,
  month,
  onCollapse,
}: {
  cell: DayCell;
  month: FounderMonth;
  onCollapse: () => void;
}) => {
  const week = month.weeks[cell.wk];
  const [open, setOpen] = useState<{
    item: PipelineItem;
    step: PipelineStep;
  } | null>(null);
  useEffect(() => {
    setOpen(null);
  }, [cell.y, cell.m, cell.d]);
  if (!week) return null;
  const items = dayExpandItems(cell, month);
  const dateLabel = formatMd(cell.m, cell.d);
  const stored = month.sources === "board_months";

  return (
    <section
      aria-label={`周层 ${week.label}`}
      className="col-span-7 my-1 overflow-hidden rounded-[10px] border-2 border-dashed border-[#8aa4c0] bg-white text-card-foreground dark:border-[#3d5f80] dark:bg-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e6e6e4] bg-[#e8f0fa] px-4 py-3 dark:border-border dark:bg-[#152433]">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
          <StatusBadge week={week} />
          <h3 className="text-[15px] font-semibold leading-none tracking-tight text-[#1a1a1a] dark:text-foreground">
            {week.label}
          </h3>
          <p className="text-[12px] leading-none text-[#666] dark:text-muted-foreground">
            <span>{week.range}</span>
            <span className="mx-2 text-[#aeaeb2]">·</span>
            <span>{week.inMonth}</span>
            <span className="mx-2 text-[#aeaeb2]">·</span>
            <span className="font-medium text-[#1a4a7a] dark:text-[#c5d9ef]">
              跟日 {dateLabel}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-[#1a4a7a] bg-white px-3 py-1 text-[13px] font-medium text-[#1a4a7a] hover:bg-[#e8f0fa] dark:border-[#8eb4d4] dark:bg-transparent dark:text-[#c5d9ef]"
            onClick={onCollapse}
          >
            收起周层
          </button>
        </div>
      </div>
      <div className="px-4 py-4 max-md:px-3">
        {items.length === 0 ? (
          stored ? (
            <p className={TYPE.empty}>
              这一天库里还没有日格。不是静态包被清空。
            </p>
          ) : (
            <p className={TYPE.empty}>当日没有相关条</p>
          )
        ) : (
          <div className="flex flex-col gap-3">
            {items.map((item) => (
              <PipelineCard
                key={item.id}
                item={item}
                onOpen={(step) => setOpen({ item, step })}
              />
            ))}
          </div>
        )}
      </div>
      {open ? (
        <StepDialog
          item={open.item}
          step={open.step}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
};

const CurrentNodeLine = ({ runId }: { runId?: number }) => {
  const { currentNodeTextFor } = useBoardWorkflow();
  const text = currentNodeTextFor(runId);
  if (!text) return null;
  return (
    <p className="mb-2 text-[12px] font-medium text-[#1a4a7a] dark:text-[#c5d9ef]">
      {text}
    </p>
  );
};

const PipelineCard = ({
  item,
  onOpen,
}: {
  item: PipelineItem;
  onOpen: (step: PipelineStep) => void;
}) => {
  const { items } = useBoardLegend();
  const face = itemFace(item, items);
  return (
    <article
      style={{ borderLeftColor: face.tone.rail, ...face.washStyle }}
      className={cn(
        "rounded-lg border border-[#e6e6e4] border-l px-4 py-3 dark:border-border",
        face.wash,
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h5 className={cn("min-w-0 flex-1", TYPE.title)} title={face.theme}>
          {face.theme}
        </h5>
        <div
          role="group"
          aria-label="分类标签"
          className="flex max-w-[48%] shrink-0 flex-wrap items-center justify-end gap-1"
        >
          <FaceChips face={face} />
        </div>
      </div>
      {item.body ? (
        <p className={cn("mb-3 whitespace-pre-wrap", TYPE.body)}>{item.body}</p>
      ) : null}
      <CurrentNodeLine runId={item.workflowRunId} />
      <ol
        aria-label={`${item.theme}进度`}
        className="pointer-events-none flex gap-0 overflow-x-auto px-1 py-2"
      >
        {item.steps.map((step, index) => {
          const detail =
            step.detail === item.theme
              ? ""
              : stepDetail(step.detail, step.mark);
          const prev = index > 0 ? item.steps[index - 1] : null;
          const blocked = blockedCopy(step);
          return (
            <li
              key={step.name}
              className="relative flex min-w-[4.4rem] flex-1 flex-col items-center overflow-visible px-1 text-center"
            >
              {prev ? (
                <span
                  aria-hidden
                  className={cn(
                    "absolute top-[0.42rem] right-[calc(50%+0.55rem)] left-[calc(-50%+0.55rem)] z-0 h-[2px]",
                    prev.mark === "done"
                      ? "bg-[#0a5c36]"
                      : "bg-[#e6e6e4] dark:bg-border",
                  )}
                />
              ) : null}
              <button
                type="button"
                title={step.detail}
                aria-label={`${step.name} ${stepLabel(step.mark)} ${step.detail}`}
                className="pointer-events-auto flex w-full cursor-default flex-col items-center bg-transparent"
                onClick={() => onOpen(step)}
              >
                <span
                  aria-hidden
                  className={cn(
                    "relative z-10 inline-flex size-3.5 shrink-0 items-center justify-center",
                    blocked
                      ? cn("rounded-full border-2", DOT_BLOCKED)
                      : PHASE_TEXT[stepPhase(step.mark)],
                  )}
                >
                  {blocked ? null : (
                    <StatusGlyph phase={stepPhase(step.mark)} />
                  )}
                </span>
                <span
                  className={cn(
                    blocked
                      ? TYPE.nodeBlocked
                      : step.mark === "now"
                        ? TYPE.nodeNow
                        : TYPE.node,
                  )}
                >
                  {step.name}
                </span>
                <span
                  className={cn(
                    blocked
                      ? TYPE.bodyBlocked
                      : step.mark === "done"
                        ? TYPE.bodyDone
                        : step.mark === "now"
                          ? TYPE.bodyNow
                          : TYPE.body,
                    !blocked && step.mark === "now" && "font-semibold",
                  )}
                >
                  {stepLabel(step.mark)}
                </span>
                {detail ? (
                  <span
                    className={cn(
                      "mt-1 line-clamp-3",
                      blocked
                        ? TYPE.bodyBlocked
                        : step.mark === "now"
                          ? TYPE.bodyNow
                          : TYPE.body,
                    )}
                  >
                    {detail}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>
    </article>
  );
};

const StepDialog = ({
  item,
  step,
  onClose,
}: {
  item: PipelineItem;
  step: PipelineStep;
  onClose: () => void;
}) => {
  useOverlayScrollLock(true);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-[rgba(20,24,28,0.55)]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="founder-step-title"
        className="fixed top-1/2 left-1/2 z-10 flex max-h-[min(78vh,560px)] w-[min(440px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-[#e6e6e4] bg-white text-card-foreground shadow-md dark:border-border dark:bg-card"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#e6e6e4] px-4 pt-4 pb-3 dark:border-border">
          <div className="min-w-0">
            <h3 id="founder-step-title" className="text-[17px] font-semibold">
              {step.name}
            </h3>
            <ItemFaceLine item={item} extra={stepLabel(step.mark)} />
          </div>
          <button
            type="button"
            className="size-8 shrink-0 rounded-md border border-[#e6e6e4] bg-white text-muted-foreground hover:bg-[#f3f3f1] dark:border-border dark:bg-card"
            aria-label="关闭"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3 text-[13px]">
          {item.body ? (
            <p className="mb-3 whitespace-pre-wrap leading-relaxed">
              {item.body}
            </p>
          ) : null}
          <StepBody step={step} />
          {item.note ? (
            <p className="mt-3 text-[12px] text-muted-foreground">
              备注：{item.note}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ArtifactTitle = ({ title }: { title?: string }) =>
  title ? (
    <p className="mb-1 text-[12px] font-medium text-muted-foreground">
      {title}
    </p>
  ) : null;

const ArtifactLinkList = ({
  links,
}: {
  links: { label: string; url: string }[];
}) => (
  <ul className="flex flex-col gap-2">
    {links.map((link) => (
      <li key={`${link.label}-${link.url}`}>
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-[#1a4a7a] underline dark:text-[#c5d9ef]"
        >
          {link.label}
        </a>
        {isImageUrl(link.url) ? (
          <img
            src={link.url}
            alt=""
            className="mt-1 max-h-40 rounded border border-border"
          />
        ) : null}
      </li>
    ))}
  </ul>
);

const StepBody = ({ step }: { step: PipelineStep }) => {
  const artifact = step.artifact;
  const status = stepDetail(step.detail, step.mark);
  const body = artifact?.body?.trim();
  const links = artifact?.links ?? [];
  if (artifact?.kind === "text") {
    return (
      <div>
        <ArtifactTitle title={artifact.title} />
        {body ? (
          <p className="whitespace-pre-wrap leading-relaxed">{artifact.body}</p>
        ) : (
          <p className="leading-relaxed text-muted-foreground">暂无正文</p>
        )}
        {links.length > 0 ? (
          <div className="mt-2">
            <ArtifactLinkList links={links} />
          </div>
        ) : null}
      </div>
    );
  }
  if (artifact?.kind === "links") {
    return (
      <div>
        <ArtifactTitle title={artifact.title} />
        {body ? (
          <p className="mb-2 whitespace-pre-wrap leading-relaxed">
            {artifact.body}
          </p>
        ) : null}
        {links.length > 0 ? (
          <ArtifactLinkList links={links} />
        ) : (
          <p className="leading-relaxed text-muted-foreground">暂无飞书链</p>
        )}
      </div>
    );
  }
  if (artifact?.kind === "empty" || !status) {
    if (step.mark === "closed") {
      return <p className={TYPE.empty}>未开</p>;
    }
    return (
      <p className="whitespace-pre-wrap leading-relaxed">
        {status || stepLabel(step.mark)}
      </p>
    );
  }
  return <p className="whitespace-pre-wrap leading-relaxed">{status}</p>;
};

const ItemFaceLine = ({
  item,
  extra,
}: {
  item: PipelineItem;
  extra?: string;
}) => {
  const { items } = useBoardLegend();
  const face = itemFace(item, items);
  return (
    <p className="mt-1 flex flex-wrap items-center gap-1 text-[12px]">
      <FaceChips face={face} rounded="rounded-md" />
      {extra ? <span className="text-muted-foreground">{extra}</span> : null}
    </p>
  );
};

const StatusBadge = ({ week }: { week: WeekData }) => {
  const tone =
    week.status === "now"
      ? "bg-[#e8f0fa] text-[#1a4a7a] dark:bg-[#152433] dark:text-[#c5d9ef]"
      : week.status === "past"
        ? "bg-muted text-foreground"
        : "bg-muted text-muted-foreground";
  const label =
    week.status === "now"
      ? "本周"
      : week.status === "past"
        ? "过去"
        : week.statusText;
  return (
    <span
      className={cn(
        "rounded px-2 py-1 text-[12px] leading-none font-medium",
        tone,
      )}
    >
      {label}
    </span>
  );
};

const ChipDrawer = ({
  month,
  state,
  onClose,
  onOpenChip,
  onExpandDay,
}: {
  month: FounderMonth;
  state: DrawerState;
  onClose: () => void;
  onOpenChip: (chip: Chip) => void;
  onExpandDay: () => void;
}) => {
  useOverlayScrollLock(true);
  const { currentNodeTextFor } = useBoardWorkflow();
  const title =
    state.kind === "detail"
      ? state.chip.short || "条目详情"
      : `${formatMd(state.cell.m, state.cell.d)} · 全部短条`;
  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-[rgba(20,24,28,0.55)]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="founder-chip-title"
        className="fixed top-1/2 left-1/2 z-10 flex max-h-[min(78vh,560px)] w-[min(420px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-[#e6e6e4] bg-white text-card-foreground shadow-md max-md:top-auto max-md:right-0 max-md:bottom-0 max-md:left-0 max-md:max-h-[70vh] max-md:w-full max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-t-[12px] max-md:rounded-b-none dark:border-border dark:bg-card"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#e6e6e4] px-4 pt-4 pb-3 dark:border-border">
          <h3
            id="founder-chip-title"
            className="text-[17px] leading-snug font-semibold"
          >
            {title}
          </h3>
          <button
            type="button"
            className="size-8 shrink-0 rounded-md border border-[#e6e6e4] bg-white text-muted-foreground hover:bg-[#f3f3f1] dark:border-border dark:bg-card"
            aria-label="关闭弹层"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3 text-[13px]">
          {state.kind === "detail" ? (
            <DetailBody chip={state.chip} cell={state.cell} month={month} />
          ) : (
            <ul>
              {state.chips.map((chip, index) => {
                const current = currentNodeTextFor(chip.workflowRunId);
                const presented = presentChip(chip, state.cell, month);
                return (
                  <li
                    key={chip.id}
                    className={cn(
                      "flex items-center gap-2 py-2 text-[13px]",
                      index > 0 &&
                        "border-t border-[#e6e6e4] dark:border-border",
                    )}
                  >
                    <StatusGlyph phase={presented.phase} tone />
                    <LineBadge lineKey={chip.lineKey} label={chip.line} />
                    <button
                      type="button"
                      className="min-w-0 truncate text-left text-[13px] font-medium text-[#1a4a7a] hover:underline dark:text-[#c5d9ef]"
                      onClick={() => onOpenChip(chip)}
                    >
                      {chip.short} — {chip.title}
                      {current ? ` · ${current}` : ""}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-[#e6e6e4] bg-[#f7f7f5] px-4 py-3 dark:border-border dark:bg-muted/40">
          <button
            type="button"
            className="rounded-md border border-[#e6e6e4] bg-white px-3 py-1 text-[13px] font-medium text-[#1a4a7a] hover:bg-[#f3f3f1] dark:border-border dark:bg-card dark:text-[#c5d9ef]"
            onClick={onClose}
          >
            关闭
          </button>
          <button
            type="button"
            className="rounded-md border border-[#1a4a7a] bg-[#1a4a7a] px-3 py-1 text-[13px] font-medium text-white hover:bg-[#153e68]"
            onClick={onExpandDay}
          >
            展开该日
          </button>
        </div>
      </div>
    </div>
  );
};

const FaceChips = ({
  face,
  rounded = "rounded-[3px]",
}: {
  face: ReturnType<typeof itemFace>;
  rounded?: string;
}) => {
  const { items } = useBoardLegend();
  const you = findLegendItem(items, { lineKey: "you" });
  return (
    <>
      <span
        title={face.businessLabel}
        className={cn(
          rounded,
          "px-2 py-1 text-[12px] leading-none font-medium",
          face.tag,
        )}
        style={face.tagStyle}
      >
        {face.businessLabel}
      </span>
      {face.matterBeside ? (
        <span
          className={cn(
            rounded,
            "px-2 py-1 text-[12px] leading-none font-medium",
            FACE_TAG.you,
          )}
          style={faceTagStyle(you)}
        >
          交流
        </span>
      ) : null}
    </>
  );
};

const DetailBody = ({
  chip,
  cell,
  month,
}: {
  chip: Chip;
  cell: DayCell;
  month: FounderMonth;
}) => {
  const mapped = categoryOf(chip.line);
  const presented = presentChip(chip, cell, month);
  const field =
    mapped?.kind === "idea"
      ? "产品想法"
      : mapped?.kind === "matter"
        ? "事项"
        : "业务线";
  return (
    <>
      <CurrentNodeLine runId={chip.workflowRunId} />
      <DrawerRow label="题名">{chip.title}</DrawerRow>
      <DrawerRow label={field}>
        <LineBadge lineKey={chip.lineKey} label={chip.line} />
      </DrawerRow>
      <DrawerRow label="状态">
        {presented.statusText ? (
          <span className="inline-flex items-center gap-2">
            <StatusGlyph phase={presented.phase} tone />
            {presented.statusText}
          </span>
        ) : (
          <span className={TYPE.empty}>还没写状态</span>
        )}
      </DrawerRow>
      <DrawerRow label={chip.body ? "文案" : "交流要点"}>
        {chip.body || chip.kayou || (
          <span className={TYPE.empty}>没有文案</span>
        )}
      </DrawerRow>
      <DrawerRow label="日期">
        {formatMd(cell.m, cell.d)} · {cell.wk} · 周{dowLabel(cell)}
      </DrawerRow>
    </>
  );
};

const DrawerRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="mb-3">
    <span className="mb-1 block text-[12px] font-medium text-muted-foreground">
      {label}
    </span>
    {children}
  </div>
);

const LineBadge = ({ lineKey, label }: { lineKey: LineKey; label: string }) => {
  const { items } = useBoardLegend();
  const mapped = categoryOf(label);
  const shown = mapped?.label ?? label;
  const legend = findLegendItem(items, { lineKey });
  const paint = mapped?.kind === "idea" ? CHIP_BG.idea : CHIP_BG[lineKey];
  return (
    <span
      className={cn(
        "inline-block rounded px-2 py-1 text-[12px] font-medium",
        paint,
      )}
      style={
        mapped?.kind === "idea"
          ? barPaintStyle(legend)
          : (softChipStyle(legend) ?? barPaintStyle(legend))
      }
    >
      {shown}
    </span>
  );
};
